import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { api, formatApiError, setToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function ChangePassword() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasPassword = user?.has_password !== false;
  const mismatch = form.confirm && form.next !== form.confirm;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setToken(data.token);
      setUser({ ...user, has_password: true });
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated — other devices have been signed out");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not change password"); }
    finally { setBusy(false); }
  };

  const type = show ? "text" : "password";
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      {hasPassword ? (
        <Input type={type} required data-testid="current-password-input" value={form.current} onChange={set("current")} placeholder="Current password" className="rounded-xl h-11" />
      ) : (
        <p className="text-sm text-slate-500" data-testid="set-password-note">You sign in with Google. Create a password to also sign in with your email.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Input type={type} required minLength={8} data-testid="new-password-input" value={form.next} onChange={set("next")} placeholder="New password (8+ characters)" className="rounded-xl h-11" />
        <Input type={type} required data-testid="confirm-password-input" value={form.confirm} onChange={set("confirm")} placeholder="Confirm new password" className={`rounded-xl h-11 ${mismatch ? "border-rose-400" : ""}`} />
      </div>
      {mismatch && <p className="text-xs text-rose-500" data-testid="password-mismatch">Passwords don't match</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" data-testid="change-password-btn" disabled={busy || form.next.length < 8 || form.next !== form.confirm || (hasPassword && !form.current)}
          className="rounded-xl h-11 font-bold bg-[#FF5A79] hover:bg-[#e04865]">
          <KeyRound className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : hasPassword ? "Change password" : "Create password"}
        </Button>
        <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password-visibility" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400 hover:text-slate-700">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {show ? "Hide" : "Show"}
        </button>
      </div>
      <p className="text-xs text-slate-400">Your other devices will be signed out and we'll email you a confirmation.</p>
    </form>
  );
}
